import * as React from "react";
import "react-datepicker/dist/react-datepicker.css";
import { twMerge } from "tailwind-merge";
import {
  useFloating,
  useClick,
  useDismiss,
  useRole,
  useListNavigation,
  useInteractions,
  FloatingFocusManager,
  useTypeahead,
  offset,
  size,
  autoUpdate,
  FloatingPortal,
  safePolygon,
} from "@floating-ui/react";
import { useHoverLeave } from "./useHoverLeave";

const PopoverMenu = ({
  hoverEnabled = true,
  children,
  options = [],
  placement = "bottom-end",
  offsetY = 4,
  offsetX = 0,
  contentMinWidth,
  className,
  open: controlledOpen,
  setOpen: setControlledOpen,
  ...props
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  //   useEffect(() => {
  //     let selected = menuIndex;
  //     if (menuIndex !== defaultLabel && menuIndex?.length > 15) {
  //       selected = "Custom";
  //     }

  //     const index = options.findIndex((user) => user.value === selected);
  //     setSelectedIndex(index);
  //   }, [open, options]);

  //   const [menuIndex, setMenuIndex] = React.useState("");

  const { refs, floatingStyles, context } = useFloating({
    placement: placement,
    open: open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset({
        mainAxis: offsetY,
        alignmentAxis: offsetX,
        crossAxis: 0,
      }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            minWidth: `${rects.reference.width}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const listRef = React.useRef([]);
  const listContentRef = React.useRef(options.map((item) => item.label));
  const isTypingRef = React.useRef(false);

  const click = useClick(context, { event: "mousedown" });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "listbox" });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    // This is a large list, allow looping.
    loop: true,
  });
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    selectedIndex,
    onMatch: open ? setActiveIndex : setSelectedIndex,
    onTypingChange(isTyping) {
      isTypingRef.current = isTyping;
    },
  });

  let hoverLeave = null;
  if (hoverEnabled) {
    hoverLeave = useHoverLeave(context, {
      enabled: true, // only enable hover interactions once open
      handleClose: safePolygon(),
    });
  }

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [dismiss, role, listNav, typeahead, click, hoverLeave]
  );

  return (
    <>
      {React.isValidElement(children) &&
        React.cloneElement(
          children,
          getReferenceProps({
            ref: refs.setReference,
            ...props,
            ...children.props,
            "data-state": context.open ? "open" : "closed",
          })
        )}

      {open && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              className={`z-[99] overflow-y-auto border border-rounded rounded-md border-Silver shadow-lg  min-w-32 text-center bg-white focus:outline-none`}
              role="group"
              aria-orientation="vertical"
              aria-labelledby="menu-button"
              tabIndex="-1"
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {options.map((item, i) => (
                <div
                  className={twMerge(
                    `text-gray-700 block text-left text-sm select-none font-semibold ${
                      item.disabled
                        ? "opacity-50 cursor-not-allowed"
                        : "cursor-pointer"
                    }`,
                    className
                  )}
                  ref={(node) => {
                    listRef.current[i] = node;
                  }}
                  style={{
                    minWidth: contentMinWidth,
                    background: item.disabled
                      ? "#ffffff"
                      : i === activeIndex
                      ? "#F3F3F3"
                      : selectedIndex === i
                      ? "#ebebeb"
                      : "#ffffff",
                  }}
                  role="menuitem"
                  tabIndex={i === activeIndex ? 0 : -1}
                  aria-selected={i === selectedIndex && i === activeIndex}
                  aria-disabled={item.disabled}
                  id={item.value}
                  key={item.value}
                  {...getItemProps({
                    // Handle pointer select.
                    onClick(e) {
                      if (item.onClick) {
                        setOpen(false);
                        item.onClick(item);
                      }
                    },
                    // Handle keyboard select.
                    onKeyDown(event) {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        setSelectedIndex(i);
                        if (item.onClick) {
                          setOpen(false);
                          item.onClick(item);
                        }
                      }

                      if (event.key === " " && !isTypingRef.current) {
                        event.preventDefault();
                        setSelectedIndex(i);
                      }
                    },
                  })}
                >
                  {item.label}
                </div>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
};
export default PopoverMenu;
